import { createContext, useContext, useState, type ReactNode } from 'react';
import { clearStoredAffiliateToken, getStoredAffiliateToken, loginAffiliate, logoutAffiliate } from '../lib/api';

// Acesso do influenciador — SEM conta, senha, cadastro ou confirmação de
// e-mail (pedido explícito do proprietário). Login é SÓ e-mail, verificado
// contra uma planilha do Google mantida pelo proprietário (ver
// worker/src/lib/affiliateSheet.mjs); o resultado é um token opaco
// guardado no localStorage deste navegador. Nada a ver com o Supabase
// Auth (ver AdminAuthContext.tsx) — dois mecanismos completamente
// separados.

interface AffiliateSessionState {
  isLoggedIn: boolean;
  login: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  // Chamado pelas páginas quando uma chamada autenticada devolve 401
  // (sessão expirada/revogada) — limpa a sessão local sem tentar revogar
  // de novo no backend (já não vale mais lá).
  invalidate: () => void;
}

const AffiliateSessionContext = createContext<AffiliateSessionState | undefined>(undefined);

export function AffiliateSessionProvider({ children }: { children: ReactNode }) {
  const [isLoggedIn, setIsLoggedIn] = useState(() => Boolean(getStoredAffiliateToken()));

  const value: AffiliateSessionState = {
    isLoggedIn,
    login: async (email) => {
      await loginAffiliate(email);
      setIsLoggedIn(true);
    },
    logout: async () => {
      await logoutAffiliate();
      setIsLoggedIn(false);
    },
    invalidate: () => {
      clearStoredAffiliateToken();
      setIsLoggedIn(false);
    },
  };

  return <AffiliateSessionContext.Provider value={value}>{children}</AffiliateSessionContext.Provider>;
}

export function useAffiliateSession(): AffiliateSessionState {
  const context = useContext(AffiliateSessionContext);
  if (!context) {
    throw new Error('useAffiliateSession must be used within an AffiliateSessionProvider');
  }
  return context;
}
